import { FormEvent, useState } from 'react'
import { ArrowUpRight, LoaderCircle, Mail, MapPin, Phone } from 'lucide-react'
import PageHero from '../components/PageHero'
import SEO from '../components/SEO'
import SectionHeading from '../components/SectionHeading'
import { localBusinessSchema } from '../config/schema'
import { brand, locations, serviceTypes } from '../data/site'
import { imageSize } from '../data/images'
import Recaptcha from '../components/Recaptcha'
import { FormsApiError, submitContactForm, submitInquiryForm } from '../services/formsApi'
import './ContactConsent.css'

const topics = [
  'General question',
  'Schedule a tour',
  'Professional waitlist',
  'Directory question',
] as const

type Topic = (typeof topics)[number]
type ServiceType = (typeof serviceTypes)[number] | ''

type ContactForm = {
  name: string
  email: string
  phone: string
  topic: Topic
  serviceType: ServiceType
  otherServiceDescription: string
  message: string
  consent: boolean
  company: string
}

type FieldErrors = Partial<Record<keyof ContactForm, string>>

const initialForm: ContactForm = {
  name: '',
  email: '',
  phone: '',
  topic: 'General question',
  serviceType: '',
  otherServiceDescription: '',
  message: '',
  consent: false,
  company: '',
}

function normalizeText(value: string) {
  return value.replaceAll('\u0000', '').trim()
}

function isLinkHeavy(value: string) {
  return /(?:https?:\/\/|www\.)/i.test(value)
}

function validateForm(form: ContactForm) {
  const errors: FieldErrors = {}
  const normalized = {
    ...form,
    name: normalizeText(form.name),
    email: normalizeText(form.email).toLowerCase(),
    phone: normalizeText(form.phone),
    otherServiceDescription: normalizeText(form.otherServiceDescription),
    message: normalizeText(form.message),
    company: normalizeText(form.company),
  }
  const isInquiry = normalized.topic === 'Professional waitlist'

  if (normalized.name.length < 2) errors.name = 'Please enter your name.'
  if (normalized.name.length > 100) errors.name = 'Please use 100 characters or fewer.'
  if (/(?:https?:\/\/|www\.)/i.test(normalized.name)) errors.name = 'Please enter a valid name.'

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized.email)) {
    errors.email = 'Please enter a valid email address.'
  }

  if (normalized.phone) {
    const phoneDigits = normalized.phone.replace(/\D/g, '')
    if (!/^[+()\d.\s-]+$/.test(normalized.phone) || phoneDigits.length < 7 || phoneDigits.length > 15) {
      errors.phone = 'Please enter a valid phone number.'
    }
  }

  if (!topics.includes(normalized.topic)) errors.topic = 'Please select an inquiry type.'

  if (isInquiry && !serviceTypes.includes(normalized.serviceType as (typeof serviceTypes)[number])) {
    errors.serviceType = 'Please select your primary service type.'
  }

  if (isInquiry && normalized.serviceType === 'Other') {
    if (normalized.otherServiceDescription.length < 2) {
      errors.otherServiceDescription = 'Please describe your service.'
    }
    if (normalized.otherServiceDescription.length > 120) {
      errors.otherServiceDescription = 'Please use 120 characters or fewer.'
    }
  }

  if (normalized.message.length < 10) errors.message = 'Please enter at least 10 characters.'
  if (normalized.message.length > 5000) errors.message = 'Please use 5,000 characters or fewer.'
  if (isLinkHeavy(normalized.message)) {
    errors.message = 'For security reasons, links cannot be included in this form.'
  }

  if (!normalized.consent) {
    errors.consent = 'Please agree to the Privacy Policy before submitting.'
  }

  return { errors, normalized, isInquiry }
}

export default function Contact() {
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const [statusMessage, setStatusMessage] = useState('')

  function updateField<K extends keyof ContactForm>(field: K, value: ContactForm[K]) {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'serviceType' && value !== 'Other' ? { otherServiceDescription: '' } : {}),
    }))
    setErrors((current) => ({ ...current, [field]: undefined }))
    if (status !== 'sending') {
      setStatus('idle')
      setStatusMessage('')
    }
  }

  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaResetKey, setCaptchaResetKey] = useState(0)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (status === 'sending') return

    const { errors: nextErrors, normalized, isInquiry } = validateForm(form)
    setErrors(nextErrors)

    const firstError = Object.keys(nextErrors)[0] as keyof ContactForm | undefined
    if (firstError) {
      setStatus('error')
      setStatusMessage('Please review the highlighted fields and try again.')
      document.getElementById(`contact-${firstError}`)?.focus()
      return
    }

    setStatus('sending')
    setStatusMessage('Securely sending your message…')

    try {
      if (!captchaToken) {
        throw new Error('Please complete the reCAPTCHA verification.')
      }

      if (isInquiry) {
        await submitInquiryForm({
          name: normalized.name,
          email: normalized.email,
          phone: normalized.phone,
          serviceType: normalized.serviceType,
          otherServiceDescription:
            normalized.serviceType === 'Other' ? normalized.otherServiceDescription : '',
          message: normalized.message,
          website: normalized.company,
          consent: normalized.consent,
          recaptchaToken: captchaToken,
        })
      } else {
        await submitContactForm({
          name: normalized.name,
          email: normalized.email,
          phone: normalized.phone,
          subject: normalized.topic,
          message: normalized.message,
          website: normalized.company,
          consent: normalized.consent,
          recaptchaToken: captchaToken,
        })
      }

      setStatus('success')
      setStatusMessage('Thank you! Your inquiry has been received. Our team will be in touch soon.')
      setForm(initialForm)
      setErrors({})
      setCaptchaToken('')
      setCaptchaResetKey((value) => value + 1)
    } catch (error) {
      setStatus('error')
      setStatusMessage(
        error instanceof FormsApiError || error instanceof Error
          ? error.message
          : 'We could not send your message. Please try again in a few minutes.',
      )
    }
  }

  const isProfessionalInquiry = form.topic === 'Professional waitlist'

  return (
    <>
      <SEO page="contact" structuredData={localBusinessSchema} />
      <PageHero index="06" eyebrow="Contact Diamond Suites Ocala" title="Let’s start a conversation." description="Ask a question, schedule a tour, or join the professional waitlist." image="community-main.jpg" imagePosition="50% 48%" />
      <section className="section">
        <div className="container contact-grid">
          <div>
            <SectionHeading index="01" eyebrow="Diamond Suites Ocala" title="We would love to hear from you" />
            <p>Reach out for general information, suite leasing questions, or help finding the right professional.</p>
            <div className="contact-cards">
              <a href={brand.mapUrl} target="_blank" rel="noopener noreferrer"><MapPin /><span><strong>Visit</strong>{brand.addressLine1}<br />{brand.addressLine2}</span></a>
              <a href={brand.phoneHref}><Phone /><span><strong>Call</strong>{brand.phoneDisplay}</span></a>
              <a href={`mailto:${brand.email}`}><Mail /><span><strong>Email</strong>{brand.email}</span></a>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit} aria-busy={status === 'sending'} noValidate>
            <p className="contact-form__note">Required fields are marked with an asterisk.</p>
            <div className="form-row">
              <label htmlFor="contact-name">
                <span className="field-label">Name <b aria-hidden="true">*</b></span>
                <input id="contact-name" name="name" required minLength={2} maxLength={100} value={form.name} onChange={(event) => updateField('name', event.target.value)} autoComplete="name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'contact-name-error' : undefined} />
                {errors.name && <span className="field-error" id="contact-name-error">{errors.name}</span>}
              </label>
              <label htmlFor="contact-email">
                <span className="field-label">Email <b aria-hidden="true">*</b></span>
                <input id="contact-email" name="email" required type="email" maxLength={254} value={form.email} onChange={(event) => updateField('email', event.target.value)} autoComplete="email" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'contact-email-error' : undefined} />
                {errors.email && <span className="field-error" id="contact-email-error">{errors.email}</span>}
              </label>
            </div>
            <div className="form-row">
              <label htmlFor="contact-phone">
                <span className="field-label">Phone <em>(optional)</em></span>
                <input id="contact-phone" name="phone" type="tel" inputMode="tel" maxLength={40} value={form.phone} onChange={(event) => updateField('phone', event.target.value)} autoComplete="tel" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'contact-phone-error' : undefined} />
                {errors.phone && <span className="field-error" id="contact-phone-error">{errors.phone}</span>}
              </label>
              <label htmlFor="contact-topic">
                <span className="field-label">Inquiry type <b aria-hidden="true">*</b></span>
                <select id="contact-topic" name="topic" required value={form.topic} onChange={(event) => updateField('topic', event.target.value as Topic)} aria-invalid={Boolean(errors.topic)} aria-describedby={errors.topic ? 'contact-topic-error' : undefined}>
                  {topics.map((topic) => <option key={topic}>{topic}</option>)}
                </select>
                {errors.topic && <span className="field-error" id="contact-topic-error">{errors.topic}</span>}
              </label>
            </div>

            {isProfessionalInquiry && (
              <div className="form-conditional" aria-live="polite">
                <label htmlFor="contact-serviceType">
                  <span className="field-label">Service type <b aria-hidden="true">*</b></span>
                  <select id="contact-serviceType" name="serviceType" required value={form.serviceType} onChange={(event) => updateField('serviceType', event.target.value as ServiceType)} aria-invalid={Boolean(errors.serviceType)} aria-describedby={errors.serviceType ? 'contact-serviceType-error' : undefined}>
                    <option value="" disabled>Select your primary service</option>
                    {serviceTypes.map((serviceType) => <option key={serviceType}>{serviceType}</option>)}
                  </select>
                  {errors.serviceType && <span className="field-error" id="contact-serviceType-error">{errors.serviceType}</span>}
                </label>
                {form.serviceType === 'Other' && (
                  <label htmlFor="contact-otherServiceDescription">
                    <span className="field-label">Please describe your service <b aria-hidden="true">*</b></span>
                    <input id="contact-otherServiceDescription" name="otherServiceDescription" required minLength={2} maxLength={120} value={form.otherServiceDescription} onChange={(event) => updateField('otherServiceDescription', event.target.value)} aria-invalid={Boolean(errors.otherServiceDescription)} aria-describedby={errors.otherServiceDescription ? 'contact-otherServiceDescription-error' : undefined} />
                    {errors.otherServiceDescription && <span className="field-error" id="contact-otherServiceDescription-error">{errors.otherServiceDescription}</span>}
                  </label>
                )}
              </div>
            )}

            <label className="honeypot" htmlFor="contact-company" aria-hidden="true">
              Company
              <input id="contact-company" name="website" tabIndex={-1} aria-hidden="true" autoComplete="off" value={form.company} onChange={(event) => updateField('company', event.target.value)} />
            </label>
            <label htmlFor="contact-message">
              <span className="field-label">Message <b aria-hidden="true">*</b></span>
              <textarea id="contact-message" name="message" required minLength={10} maxLength={5000} rows={6} value={form.message} onChange={(event) => updateField('message', event.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'contact-message-error contact-privacy-note' : 'contact-privacy-note'} />
              {errors.message && <span className="field-error" id="contact-message-error">{errors.message}</span>}
            </label>
            <p className="contact-form__privacy" id="contact-privacy-note">
              Please do not submit Social Security numbers, payment card information, medical
              information, or other sensitive personal information through this form.
            </p>
            <label id="dso-consent-control" htmlFor="contact-consent">
              <input
                id="contact-consent"
                name="consent"
                type="checkbox"
                required
                checked={form.consent}
                onChange={(event) => updateField('consent', event.target.checked)}
                aria-invalid={Boolean(errors.consent)}
                aria-describedby={errors.consent ? 'contact-consent-error' : undefined}
              />

              <span id="dso-consent-box" aria-hidden="true" />

              <span id="dso-consent-copy">
                I agree to the <a href="/privacy-policy/">Privacy Policy</a> and consent to being contacted regarding this inquiry.
              </span>
            </label>

            {errors.consent && (
              <span className="field-error" id="contact-consent-error">
                {errors.consent}
              </span>
            )}

            <Recaptcha
              onTokenChange={setCaptchaToken}
              resetKey={captchaResetKey}
            />
            <button className="button contact-form__submit" type="submit" disabled={status === 'sending'}>
              {status === 'sending' && <LoaderCircle className="contact-form__spinner" aria-hidden="true" />}
              {status === 'sending' ? 'Sending message…' : 'Send message'}
            </button>
            <p className={`form-status${status === 'success' || status === 'error' ? ` form-status--${status}` : ''}`} role={status === 'error' ? 'alert' : 'status'} aria-live="polite" aria-atomic="true">{statusMessage}</p>
          </form>
        </div>
      </section>

      <section className="section section--ink">
        <div className="container">
          <SectionHeading index="02" eyebrow="Central Florida locations" title="Three Diamond Suites destinations" align="center" />
          <div className="locations-grid">
            {locations.map((location) => (
              <article key={location.name}>
                <img src={`${import.meta.env.BASE_URL}images/${location.logo}`} alt={location.name} {...imageSize(location.logo)} loading="lazy" />
                <p>{location.address}</p>
                <a href={`tel:${location.phone.replace(/\D/g, '')}`}>{location.phone}</a>
                <a
                  className="text-link location-website-link"
                  href={location.href === '/' ? import.meta.env.BASE_URL : location.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit website <ArrowUpRight size={16} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
